import React, { useMemo, useState } from 'react';
import { useContent } from '../i18n/LanguageContext';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { TrendingUp, Clock, ShieldCheck, ArrowRight, Wallet, Download } from 'lucide-react';
import { generateRoiPdf } from '../utils/roiPdf';

const fmtCHF = (n) => new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));

export default function RoiCalculator({ onDemo }) {
  const c = useContent();
  const r = c.roi;
  const cfg = r.cfg;

  const [assets, setAssets] = useState(cfg.assets.default);
  const [avgValue, setAvgValue] = useState(cfg.avgValue.default);
  const [hours, setHours] = useState(cfg.hours.default);

  const calc = useMemo(() => {
    const laborAnnual = hours * cfg.hourlyCost * 52;
    const timeSaved = laborAnnual * cfg.timeReduction;
    const fleetValue = assets * avgValue;
    const lossAvoided = fleetValue * (cfg.annualLossRate * cfg.lossReduction + cfg.utilRate * cfg.utilReduction);
    const gross = timeSaved + lossAvoided;
    const cost = assets * cfg.tagMonthly * 12;
    const net = Math.max(0, gross - cost);
    const roi = cost > 0 ? Math.max(0, (net / cost) * 100) : 0;
    return { timeSaved, lossAvoided, gross, cost, net, roi };
  }, [assets, avgValue, hours, cfg]);

  const sliders = [
    { label: r.assets, val: assets, set: setAssets, cfg: cfg.assets, unit: r.unitAssets, fmt: (v) => `${v} ${r.unitAssets}` },
    { label: r.avgValue, val: avgValue, set: setAvgValue, cfg: cfg.avgValue, unit: 'CHF', fmt: (v) => `${fmtCHF(v)} CHF` },
    { label: r.hours, val: hours, set: setHours, cfg: cfg.hours, unit: r.unitHours, fmt: (v) => `${v} ${r.unitHours}` },
  ];

  return (
    <section id="roi" className="py-14 md:py-20 bg-white">
      <div className="lt-container">
        <div className="max-w-3xl mx-auto text-center reveal">
          <p className="text-sm font-semibold uppercase tracking-widest text-teal-600">{r.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">{r.title}</h2>
          <p className="mt-4 text-lg text-slate-600">{r.subtitle}</p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {/* Inputs */}
          <div className="reveal rounded-3xl border border-slate-200 bg-slate-50 p-7 md:p-9 space-y-8">
            {sliders.map((s) => (
              <div key={s.label}>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-slate-700">{s.label}</label>
                  <span className="font-display text-lg font-bold text-teal-700">{s.fmt(s.val)}</span>
                </div>
                <Slider
                  value={[s.val]}
                  min={s.cfg.min}
                  max={s.cfg.max}
                  step={s.cfg.step}
                  onValueChange={(v) => s.set(v[0])}
                  className="[&_[role=slider]]:border-teal-600 [&_[role=slider]]:bg-white"
                />
                <div className="flex justify-between mt-2 text-xs text-slate-400">
                  <span>{s.cfg.min}</span><span>{s.cfg.max}</span>
                </div>
              </div>
            ))}

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="rounded-2xl bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-teal-700"><Clock className="w-4 h-4" /><span className="text-xs font-semibold">{r.breakdownTime}</span></div>
                <p className="mt-2 font-display text-xl font-bold text-slate-900">{fmtCHF(calc.timeSaved)} CHF</p>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-cyan-700"><ShieldCheck className="w-4 h-4" /><span className="text-xs font-semibold">{r.breakdownLoss}</span></div>
                <p className="mt-2 font-display text-xl font-bold text-slate-900">{fmtCHF(calc.lossAvoided)} CHF</p>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="reveal relative rounded-3xl bg-slate-900 text-white p-7 md:p-9 overflow-hidden flex flex-col">
            <div className="absolute inset-0 grid-dots opacity-[0.08]" />
            <div className="relative">
              <p className="text-sm font-semibold uppercase tracking-widest text-teal-400">{r.resultTitle}</p>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-5xl md:text-6xl font-bold text-gradient">{fmtCHF(calc.net)}</span>
                <span className="mb-2 text-lg text-slate-300">CHF {r.perYear}</span>
              </div>

              <div className="mt-8 space-y-3">
                <Row label={r.annualSavings} value={`${fmtCHF(calc.gross)} CHF`} icon={TrendingUp} />
                <Row label={r.logitagCost} value={`−${fmtCHF(calc.cost)} CHF`} icon={Wallet} muted />
                <div className="h-px bg-white/10 my-1" />
                <Row label={r.netSavings} value={`${fmtCHF(calc.net)} CHF`} strong />
              </div>

              <div className="mt-8 rounded-2xl bg-teal-500/15 border border-teal-500/25 p-5 flex items-center justify-between">
                <span className="text-sm font-medium text-teal-200">{r.roiLabel}</span>
                <span className="font-display text-3xl font-bold text-teal-300">{Math.round(calc.roi)}%</span>
              </div>
            </div>

            <div className="relative mt-8 pt-2">
              <Button onClick={onDemo} className="btn-shine w-full h-12 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-full">
                {r.cta}<ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <button
                onClick={() => generateRoiPdf({ content: c, inputs: { assets, avgValue, hours }, calc })}
                className="mt-3 w-full h-11 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                <Download className="w-4 h-4" /> {r.pdfBtn}
              </button>
              <p className="mt-3 text-xs text-center text-slate-500">{r.disclaimer}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value, icon: Icon, muted, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`flex items-center gap-2 text-sm ${strong ? 'font-semibold text-white' : 'text-slate-300'}`}>
        {Icon && <Icon className="w-4 h-4 text-teal-400" />}{label}
      </span>
      <span className={`font-display font-bold ${strong ? 'text-teal-300 text-lg' : muted ? 'text-slate-400' : 'text-white'}`}>{value}</span>
    </div>
  );
}
